import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Popover,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    CalendarToday as CalendarIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import { toJalaali, toGregorian } from 'jalaali-js';
import { toPersianNumber } from '../utils/persianNumber';

interface PersianDatePickerProps {
    value?: string; // YYYY-MM-DD
    onChange: (date: string) => void;
    label?: string;
    minDate?: string | Date;
    maxDate?: string | Date;
}

const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const monthNames = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
];

const parseDateSafe = (val?: string): Date => {
    if (!val) return new Date();
    try {
        const parts = val.split('T')[0].split('-').map(Number);
        if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
            return new Date(parts[0], parts[1] - 1, parts[2]);
        }
        const d = new Date(val);
        return isNaN(d.getTime()) ? new Date() : d;
    } catch {
        return new Date();
    }
};

const PersianDatePicker: React.FC<PersianDatePickerProps> = ({ value, onChange, label = 'تاریخ', minDate, maxDate }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Date>(() => parseDateSafe(value));

    useEffect(() => {
        if (value) {
            const parsed = parseDateSafe(value);
            setCurrentMonth(parsed);
        }
    }, [value]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setCurrentMonth(parseDateSafe(value));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentMonth((prev) => {
            const safePrev = isNaN(prev.getTime()) ? new Date() : prev;
            const jDate = toJalaali(safePrev.getFullYear(), safePrev.getMonth() + 1, safePrev.getDate());
            let jy = jDate.jy;
            let jm = jDate.jm;

            if (direction === 'prev') {
                jm--;
                if (jm < 1) {
                    jm = 12;
                    jy--;
                }
            } else {
                jm++;
                if (jm > 12) {
                    jm = 1;
                    jy++;
                }
            }

            const gDate = toGregorian(jy, jm, 1);
            return new Date(gDate.gy, gDate.gm - 1, gDate.gd);
        });
    };

    const getDaysInMonth = () => {
        const safeMonth = isNaN(currentMonth.getTime()) ? new Date() : currentMonth;
        const jDate = toJalaali(safeMonth.getFullYear(), safeMonth.getMonth() + 1, safeMonth.getDate());
        const jy = jDate.jy;
        const jm = jDate.jm;

        const firstDayG = toGregorian(jy, jm, 1);
        const firstDayDate = new Date(firstDayG.gy, firstDayG.gm - 1, firstDayG.gd);

        const startDayOfWeek = (firstDayDate.getDay() + 1) % 7;
        const daysInMonth = jm <= 6 ? 31 : (jm <= 11 ? 30 : (((jy + 2346) % 128 < 30) ? 30 : 29));

        const days = [];
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysInMonth; i++) {
            const gDate = toGregorian(jy, jm, i);
            days.push({
                day: i,
                dateStr: `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}`
            });
        }

        return { days, jy, jm };
    };

    const { days, jy, jm } = getDaysInMonth();

    let displayValue = '';
    if (value) {
        try {
            const parts = value.split('T')[0].split('-').map(Number);
            if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
                const j = toJalaali(parts[0], parts[1], parts[2]);
                displayValue = `${toPersianNumber(j.jy)}/${toPersianNumber(String(j.jm).padStart(2, '0'))}/${toPersianNumber(String(j.jd).padStart(2, '0'))}`;
            }
        } catch {
            displayValue = '';
        }
    }

    return (
        <Box>
            <TextField
                label={label}
                value={displayValue}
                onClick={handleOpen}
                placeholder="انتخاب تاریخ"
                size="small"
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            {value && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange('');
                                    }}
                                    title="پاک کردن تاریخ"
                                    edge="end"
                                    sx={{ mr: 0.5 }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton onClick={handleOpen} edge="end" size="small">
                                <CalendarIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: { fontFamily: 'Vazir, sans-serif', cursor: 'pointer', height: '48px', borderRadius: '12px' }
                }}
                InputLabelProps={{
                    sx: { fontFamily: 'Vazir, sans-serif' },
                    shrink: Boolean(displayValue),
                }}
                inputProps={{
                    sx: { cursor: 'pointer', fontFamily: 'Vazir, sans-serif' }
                }}
                fullWidth
            />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ p: 2, width: 300, dir: 'rtl' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigateMonth('prev')} size="small">
                            <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                            {monthNames[jm - 1]} {toPersianNumber(jy)}
                        </Typography>
                        <IconButton onClick={() => navigateMonth('next')} size="small">
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                        {weekDays.map(d => (
                            <Typography key={d} align="center" variant="caption" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                {d}
                            </Typography>
                        ))}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {days.map((d, i) => {
                            if (!d) return <Box key={i} />;
                            const isSelected = value === d.dateStr;
                            const isToday = d.dateStr === new Date().toISOString().split('T')[0];
                            
                            let isDisabled = false;
                            if (minDate) {
                                const minDateObj = new Date(minDate);
                                minDateObj.setHours(0, 0, 0, 0);
                                const dDateObj = new Date(d.dateStr);
                                dDateObj.setHours(0, 0, 0, 0);
                                if (dDateObj < minDateObj) {
                                    isDisabled = true;
                                }
                            }
                            if (maxDate) {
                                const maxDateObj = new Date(maxDate);
                                maxDateObj.setHours(0, 0, 0, 0);
                                const dDateObj = new Date(d.dateStr);
                                dDateObj.setHours(0, 0, 0, 0);
                                if (dDateObj > maxDateObj) {
                                    isDisabled = true;
                                }
                            }

                            return (
                                <Box
                                    key={i}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        onChange(d.dateStr);
                                        handleClose();
                                    }}
                                    sx={{
                                        height: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        borderRadius: '50%',
                                        bgcolor: isSelected ? 'primary.main' : 'transparent',
                                        color: isDisabled ? 'text.disabled' : (isSelected ? 'white' : (isToday ? 'primary.main' : 'text.primary')),
                                        border: isToday && !isSelected && !isDisabled ? '1px solid' : 'none',
                                        borderColor: 'primary.main',
                                        opacity: isDisabled ? 0.5 : 1,
                                        '&:hover': {
                                            bgcolor: isDisabled ? 'transparent' : (isSelected ? 'primary.dark' : 'rgba(91, 124, 250, 0.1)'),
                                        }
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                        {toPersianNumber(d.day)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default PersianDatePicker;
